import { useMemo } from 'react';
import { z } from 'zod';
import { Action, Subject } from '@erp/authz';
import {
  workbookDefinitionSchema,
  workbookRunModeSchema,
  type WorkbookDefinition,
} from '@erp/shared-types';
import {
  definitionWithFormulaText,
  definitionWithStoredFormulas,
  type TableColumns,
} from '@erp/workbooks';
import { rpcClient } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import type { WorkbookAgentPort } from './useWorkbookAgentTools';
import {
  applyWorkbookAgentOperations,
  refuseNestedReportConfig,
  WorkbookAgentEditError,
  workbookAgentOperationSchema,
} from './workbookAgentEdits';
import {
  gateReportCharts,
  type ReportChartRows,
} from './workbookAgentReportCharts';
import { agentRunSummaries } from './workbookAgentReads';
import { requireNoAgentRelationalRules } from './workbookAgentSampling';
import { workbookReportSources } from './useWorkbookReportData';
import { workbookReportRows } from './workbookReportDataset';
import { workbookReportRegistry } from './workbookReportRegistry';
import {
  followTargetOfDefinition,
  followTargetOfOperations,
  removedTabIds,
} from './workbookAgentFollow';

const STALE =
  'The workbook changed while you were editing. Read it again before editing.';
/** Tries an edit against the latest Definition this many times while the user keeps editing. */
const EDIT_ATTEMPTS = 3;

/**
 * Each Custom chart's rows: its Worksheet's Result as the Report charts it,
 * read whole when it is within the Client row limit, else none.
 */
function reportChartRows(
  workbookId: string,
  definition: WorkbookDefinition,
): ReportChartRows {
  let sources: ReturnType<typeof workbookReportSources> | null = null;
  return async (sheetId) => {
    sources ??= workbookReportSources(
      definition,
      await rpcClient.workbooks.runs({ workbookId }),
    );
    const source = sources.find((entry) => entry.key === sheetId);
    if (!source?.runId || source.path !== 'client') return undefined;
    const read = await rpcClient.workbooks.resultRows({
      workbookId,
      runId: source.runId,
      sheetId,
    });
    return read.kind === 'rows'
      ? workbookReportRows(source, read.columns, read.rows)
      : undefined;
  };
}

function checkAppliedSteps(
  definition: WorkbookDefinition,
  appliedStepIds: readonly string[],
) {
  if (
    appliedStepIds.some(
      (id) => !definition.processSteps.some((step) => step.id === id),
    )
  )
    throw new WorkbookAgentEditError(
      'Applied instruction IDs must exist in the workbook.',
    );
}

/**
 * The agent's one way to change the Workbook: builds the next Definition from
 * the latest one, gates its Custom charts, and applies it through the editor
 * as one edit (one undoable step), then saves. With `expected`, refuses
 * when the user edited since the agent read it; without, rebuilds from the
 * user's newer edit instead.
 */
export async function applyAgentEdit(
  workbookId: string,
  port: WorkbookAgentPort,
  expected: { editVersion: number; revision: number } | null,
  build: (
    current: WorkbookDefinition,
    columns: TableColumns,
  ) => WorkbookDefinition,
  appliedStepIds: readonly string[],
  /** The tab to show the user once the edit lands, if it changed one. */
  followTarget: (
    before: WorkbookDefinition,
    after: WorkbookDefinition,
  ) => string | null = followTargetOfDefinition,
) {
  for (let attempt = 0; attempt < EDIT_ATTEMPTS; attempt++) {
    const { definition: current, token, revision } = port.snapshot();
    // The edit version restarts at 0 whenever the editor mounts, so the saved
    // revision tells a token read before a reload from one read after it.
    if (
      expected !== null &&
      (token !== expected.editVersion || revision !== expected.revision)
    )
      throw new WorkbookAgentEditError(STALE);
    // Its tables' columns as the text it was built from showed them; a cell the
    // agent didn't change keeps its stored form.
    const columns = port.plan(current).tables;
    const built = definitionWithStoredFormulas(build(current, columns), {
      columns,
      base: current,
    });
    checkAppliedSteps(built, appliedStepIds);
    requireNoAgentRelationalRules(current, built);
    const { definition, warnings } = await gateReportCharts(
      current,
      built,
      reportChartRows(workbookId, built),
      workbookReportRegistry,
    );
    if (!port.applyIfUnchanged(token, definition, appliedStepIds)) {
      if (expected !== null) break;
      continue;
    }
    try {
      const target = followTarget(current, definition);
      port.follow?.(target, removedTabIds(current, definition));
    } catch (cause) {
      // Showing the tab is a courtesy; the edit already landed.
      console.warn('Workbook follow failed', cause);
    }
    const diagnostics = await port.save();
    const saved = port.snapshot();
    return {
      editVersion: saved.token,
      revision: saved.revision,
      diagnostics,
      ...(warnings.length && { warnings }),
    };
  }
  throw new WorkbookAgentEditError(STALE);
}

const appliedStepIdsSchema = z
  .array(z.string())
  .max(100)
  .default([])
  .describe(
    'IDs of process instructions implemented or reconciled by this change. Include the selected generation step. This clears their review indicators; preserve unrelated instructions.',
  );

const APPLY_RESULT =
  "Returns the saved workbook's diagnostics (problems that block a run but not the save, written for users: quote them) and warnings from checking custom charts. Fix every diagnostic before run_open_workbook.";

/** The on-page agent's writes: edits through the editor's one write path, Runs and Tasks. */
export function useWorkbookAgentWriteTools(
  workbookId: string,
  port: WorkbookAgentPort,
) {
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const [canExecute] = useCan(Action.Execute, Subject.Workbook);
  const [canCreateTask] = useCan(Action.Create, Subject.Task);
  const applyTool = useFrontendTool({
    name: 'apply_workbook',
    enabled: canUpdate,
    description: `Replace the whole workbook definition, outputs included, and autosave it: for generating or restructuring a workbook. For targeted changes prefer edit_workbook. Preserve unrelated items and use expectedEditVersion and expectedRevision from get_workbook. Write column formulas as Excel table formulas that reference this row: [@Cases], or [@[Case Price]] for names with spaces or special characters (escape [ ] # ' @ inside a name with a leading '). ${APPLY_RESULT}`,
    parameters: z.object({
      expectedEditVersion: z.number().int(),
      expectedRevision: z.number().int(),
      appliedStepIds: appliedStepIdsSchema,
      definitionJson: z
        .string()
        .max(32 * 1024 * 1024)
        .describe(
          'JSON workbook definition matching the schema returned by get_workbook(includeSchema: true).',
        ),
    }),
    awaitable: true,
    execute: (input) => {
      const written: unknown = JSON.parse(input.definitionJson);
      refuseNestedReportConfig(
        typeof written === 'object' && written !== null && 'outputs' in written
          ? written.outputs
          : undefined,
      );
      const definition = workbookDefinitionSchema.parse(written);
      return applyAgentEdit(
        workbookId,
        port,
        {
          editVersion: input.expectedEditVersion,
          revision: input.expectedRevision,
        },
        () => definition,
        input.appliedStepIds,
      );
    },
  });
  const editTool = useFrontendTool({
    name: 'edit_workbook',
    enabled: canUpdate,
    description: `Change parts of the workbook in one step the user can undo, applied to the latest version (the user's own edits since you read it are kept) and autosaved. Operations apply in order, all or nothing: upsert puts a whole item (a source, worksheet, output, task rule or instruction; formulas as Excel text) by its id, adding it when new; update replaces top-level fields of one item (e.g. a worksheet's filters, sort, lookups, columns, validations, sample or pivot; a source's filters; a report's layout, filters or paletteId); remove deletes an item with its instruction links, and a worksheet also with its file tabs and task rules; set_workbook changes the name, workbook instructions or automatic SOP updates. ${APPLY_RESULT}`,
    parameters: z.object({
      operations: z.array(workbookAgentOperationSchema).min(1).max(50),
      appliedStepIds: appliedStepIdsSchema,
    }),
    awaitable: true,
    execute: (input) =>
      applyAgentEdit(
        workbookId,
        port,
        null,
        (current, columns) =>
          applyWorkbookAgentOperations(
            definitionWithFormulaText(current, { columns }),
            input.operations,
          ),
        input.appliedStepIds,
        (_before, after) => followTargetOfOperations(input.operations, after),
      ),
  });
  const runTool = useFrontendTool({
    name: 'run_open_workbook',
    enabled: canExecute,
    description:
      "Save the workbook open in the editor and start a run of it, as the user's Run button does; the run continues in the background, so read workbook_runs for its outcome. Call only when the user requests execution. mode 'entire' reads every source again and calculates every worksheet. 'up-to' reads only the sources targetId needs and calculates it and everything upstream of it; downstream problems do not block it. An output's targetId runs up to every worksheet it reads. 'from' reuses the last run's inputs and calculates the targetId worksheet and everything downstream; it is refused when anything upstream of it changed since the last run. 'only' reuses the last run's inputs the same way and calculates the targetId worksheet alone; worksheets downstream keep their earlier results and show as stale. 'sample' calculates every worksheet over the workbook's sampling; prefer run_workbook_sample, which waits for it and returns what it found.",
    parameters: z.object({
      mode: workbookRunModeSchema.default('entire'),
      targetId: z
        .string()
        .optional()
        .describe(
          "The worksheet id for 'up-to', 'from' and 'only', or an output id for 'up-to'.",
        ),
    }),
    awaitable: true,
    execute: async (input) => {
      const run = await port.run(input.mode, input.targetId ?? null);
      return agentRunSummaries([run], port.snapshot().definition, 1)[0];
    },
  });
  const openTasksTool = useFrontendTool({
    name: 'open_run_tasks',
    enabled: canCreateTask,
    description:
      "Apply the workbook's task rules to a completed run by hand, opening a task per row of each rule's worksheet (at most 100 per rule); rows that already have their task get none. Use for a run that opened none, e.g. one from before a rule existed.",
    parameters: z.object({ runId: z.uuid() }),
    awaitable: true,
    execute: (input) =>
      rpcClient.workbooks.openRunTasks({ workbookId, runId: input.runId }),
  });
  const stepTaskTool = useFrontendTool({
    name: 'create_instruction_task',
    enabled: canCreateTask,
    description:
      'Create a task for one instruction of the process (a step a person does, e.g. "Review the pricing exceptions"), linked back to the workbook. Saves the workbook first.',
    parameters: z.object({
      stepId: z.string().min(1).max(200),
      priority: z.enum(['low', 'medium', 'high', 'urgent']).optional(),
      dueDate: z.iso.datetime().optional(),
    }),
    awaitable: true,
    execute: async (input) => {
      await port.save();
      // One tool call is one request: a retry of the same call is a new Task only if the model asks again.
      return rpcClient.workbooks.createStepTask({
        workbookId,
        ...input,
        requestId: crypto.randomUUID(),
      });
    },
  });
  return useMemo(
    () => [applyTool, editTool, runTool, openTasksTool, stepTaskTool],
    [applyTool, editTool, runTool, openTasksTool, stepTaskTool],
  );
}
